/* ==========================================================================
   BRITTEN CUSTOM: Bath clock -- the <britten-bath-clock> custom element
   ==========================================================================

   Added:       October 2026
   Author:      Claude for Matthew
   Location:    assets/britten-bath-clock.js
   Loaded by:   sections/britten-footer.liquid (<script defer>, at the end of
                the section)

   PURPOSE
   Makes the photographed dial in the workshop card tell the time in Bath,
   England, whatever time it is where the visitor is: the hour, minute and second
   hands, the day and date windows, and the clock's accessible name.

   HOW IT WORKS
   - The markup is server-rendered inside the element (snippets/britten-bath-clock.liquid):
     dial image, SVG hands at their resting position, empty day and date
     windows. This file only moves them. Hooks: data-bf-hour / -minute / -second
     (hand groups), data-bf-win-day-g / data-bf-win-date-g (the sliding window
     groups), data-bf-win-day / data-bf-win-date (the window text).
   - TIME SOURCE: Europe/London through Intl.DateTimeFormat (BST and GMT are
     handled by the browser). The London offset is worked out from formatToParts
     once a minute and added to Date.now() on every frame. The visitor's own
     clock offset is never used.
   - MOVEMENT ("mechanical"): the second hand steps five times a second (0.2s
     steps); the minute and hour hands move continuously, driven from the same
     clock. requestAnimationFrame, paused while the clock is off screen
     (IntersectionObserver).
   - INTRO, on first view: the hands sweep from rest (hour 303.3 degrees, minute
     58, second 0) to the real time over 1800ms, easeInOutCubic, always moving
     forward. At 1500ms the day and date windows roll down from -72px to 0 over
     650ms (cubic-bezier(.3,1.35,.5,1)), the date starting 140ms after the day.
     The windows roll again at midnight (Bath time). Under prefers-reduced-motion:
     reduce there is no intro at all: the hands and windows are simply right.
   - ACCESSIBILITY: role="img" on the element; aria-label is the template from
     data-bf-aria with the place, time, weekday and date filled in, updated once
     a minute (on the minute, whether or not the clock is on screen), never
     per frame. Before this script runs it names the place only, never a time.
   - The gold studio-hours arc of the design is NOT built.

   THEME DEPENDENCY
   - snippets/britten-bath-clock.liquid (the markup and its data-bf-* hooks)
   - assets/britten-footer.css (.bf .bf-clock rules: size, shadow, dial clip)
   - Libre Caslon Text, loaded (subset, non-blocking) by sections/britten-footer.liquid

   CHANGELOG
   - October 2026: Initial version (footer v2, handoff turn 5).
   ========================================================================== */

(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false; // true -> logs what the clock decided

  var TAG = 'britten-bath-clock';
  var TZ = 'Europe/London';
  var INTRO_MS = 1800;
  var ROLL_AT_MS = 1500;
  var ROLL_MS = 650;
  var ROLL_GAP_MS = 140; // the date starts this long after the day
  var ROLL_FROM = 'translateY(-72px)';
  var ROLL_EASE = 'cubic-bezier(.3,1.35,.5,1)';
  var REST_HOUR = 303.3;
  var REST_MINUTE = 58;
  var REST_SECOND = 0;
  var DAY_MS = 86400000;
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // === HELPER FUNCTIONS ===
  function log(message) {
    if (DEBUG_MODE && window.console) console.log('[Britten Bath Clock] ' + message);
  }

  var fmt = null;
  try {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
  } catch (e) {
    fmt = null;
  }

  // Milliseconds to add to a UTC timestamp to get Bath's wall clock (read the
  // result with the getUTC* methods). Worked out to the minute.
  function londonOffset(date) {
    var p = {};
    var parts = fmt.formatToParts(date);
    for (var i = 0; i < parts.length; i++) p[parts[i].type] = parts[i].value;
    var asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return Math.round((asUTC - Math.floor(date.getTime() / 1000) * 1000) / 60000) * 60000;
  }

  function easeInOutCubic(p) {
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
  }

  // From "from" to "to" degrees, always going forward round the dial.
  function forward(from, to, e) {
    return from + ((((to - from) % 360) + 360) % 360) * e;
  }

  function fill(template, values) {
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  // === MAIN LOGIC ===
  class BathClock extends HTMLElement {
    connectedCallback() {
      if (!this._ready) {
        this._ready = true;
        this._setup();
      }
      this._attach();
    }

    disconnectedCallback() {
      this._detach();
    }

    _setup() {
      var self = this;
      this._hour = this.querySelector('[data-bf-hour]');
      this._minute = this.querySelector('[data-bf-minute]');
      this._second = this.querySelector('[data-bf-second]');
      this._dayG = this.querySelector('[data-bf-win-day-g]');
      this._dateG = this.querySelector('[data-bf-win-date-g]');
      this._dayText = this.querySelector('[data-bf-win-day]');
      this._dateText = this.querySelector('[data-bf-win-date]');
      this._place = this.getAttribute('data-bf-place') || '';
      this._template = this.getAttribute('data-bf-aria') || '';
      this._lastHour = this._lastMinute = this._lastSecond = '';
      this._raf = 0;
      this._timer = 0;
      this._io = null;
      this._started = false; // the first view has happened
      this._introStart = null;
      this._windowsShown = false;
      this._dayKey = null;
      this._broken = !fmt || !this._hour || !this._minute || !this._second;
      if (this._broken) {
        log('no Intl time zone support or incomplete markup: left as a still picture');
        return;
      }

      this._reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var now = Date.now();
      this._off = londonOffset(new Date(now));
      this._offAt = now;
      this._onTick = function () {
        self._frame();
        if (self._raf) self._raf = window.requestAnimationFrame(self._onTick);
      };
      this._onVisibility = function () {
        if (!document.hidden) self._caption();
      };

      this._caption();
      if (this._reduce) {
        // No intro: the hands and the windows are right from the start.
        this._windowsShown = true;
        this._showWindows();
        this._frame();
      }
    }

    _attach() {
      if (this._broken || this._attached) return;
      this._attached = true;
      var self = this;
      document.addEventListener('visibilitychange', this._onVisibility);
      this._scheduleCaption();
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) self._visible(entries[i].isIntersecting);
        }, { threshold: 0.1 });
        this._io.observe(this);
      } else {
        this._visible(true);
      }
    }

    _detach() {
      if (!this._attached) return;
      this._attached = false;
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      this._stop();
      clearTimeout(this._timer);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    // On screen or off it. The first time it is on screen the intro starts.
    _visible(on) {
      if (on) {
        if (!this._started) {
          this._started = true;
          if (!this._reduce) {
            this._introStart = performance.now();
            this._windowsShown = true;
            this._showWindows();
            this._roll(ROLL_AT_MS);
          }
        }
        this._start();
      } else {
        this._stop();
      }
    }

    _start() {
      if (this._raf) return;
      this._raf = window.requestAnimationFrame(this._onTick);
    }

    _stop() {
      if (this._raf) window.cancelAnimationFrame(this._raf);
      this._raf = 0;
    }

    // One frame: the hands. Bath time = Date.now() plus the London offset.
    _frame() {
      var now = Date.now();
      if (Math.abs(now - this._offAt) > 60000) {
        this._off = londonOffset(new Date(now));
        this._offAt = now;
      }
      var day = (((now + this._off) % DAY_MS) + DAY_MS) % DAY_MS;
      var step = Math.floor(day / 200); // 0.2s steps
      var hd = ((day / 3600000) % 12) * 30;
      var md = ((step / 300) % 60) * 6;
      var sd = ((step * 0.2) % 60) * 6;

      if (this._introStart !== null) {
        var e = easeInOutCubic(Math.min(1, (performance.now() - this._introStart) / INTRO_MS));
        hd = forward(REST_HOUR, hd, e);
        md = forward(REST_MINUTE, md, e);
        sd = forward(REST_SECOND, sd, e);
        if (performance.now() - this._introStart >= INTRO_MS) this._introStart = null;
      }

      // three decimals is far finer than the eye, and stops a write on every frame
      var h = 'rotate(' + Math.round(hd * 1000) / 1000 + ')';
      var m = 'rotate(' + Math.round(md * 1000) / 1000 + ')';
      var s = 'rotate(' + Math.round(sd * 1000) / 1000 + ')';
      if (h !== this._lastHour) {
        this._lastHour = h;
        this._hour.setAttribute('transform', h);
      }
      if (m !== this._lastMinute) {
        this._lastMinute = m;
        this._minute.setAttribute('transform', m);
      }
      if (s !== this._lastSecond) {
        this._lastSecond = s;
        this._second.setAttribute('transform', s);
      }
    }

    // Bath's date and time as words, for the aria-label and the windows.
    _words() {
      var d = new Date(Date.now() + this._off);
      var h = d.getUTCHours();
      var mi = d.getUTCMinutes();
      var weekday = DAYS[d.getUTCDay()];
      return {
        time: (h % 12 || 12) + ':' + (mi < 10 ? '0' : '') + mi + ' ' + (h < 12 ? 'am' : 'pm'),
        weekday: weekday,
        day: weekday.slice(0, 3).toUpperCase(),
        date: String(d.getUTCDate()),
        dayKey: Math.floor(d.getTime() / DAY_MS)
      };
    }

    // Once a minute (and when the tab comes back): the accessible name, and, if
    // the day has turned over, the windows.
    _caption() {
      var now = Date.now();
      this._off = londonOffset(new Date(now));
      this._offAt = now;
      var w = this._words();
      if (this._template) {
        this.setAttribute('aria-label', fill(this._template, { place: this._place, time: w.time, weekday: w.weekday, date: w.date }));
      }
      if (this._dayKey !== null && w.dayKey !== this._dayKey) {
        this._dayKey = w.dayKey;
        if (this._windowsShown) {
          this._showWindows();
          if (this._introStart === null && this._started && !this._reduce) this._roll(0);
        }
      } else if (this._dayKey === null) {
        this._dayKey = w.dayKey;
      }
    }

    _scheduleCaption() {
      var self = this;
      clearTimeout(this._timer);
      this._timer = setTimeout(function () {
        self._caption();
        self._scheduleCaption();
      }, 60000 - (Date.now() % 60000) + 50);
    }

    _showWindows() {
      var w = this._words();
      if (this._dayText && this._dayText.textContent !== w.day) this._dayText.textContent = w.day;
      if (this._dateText && this._dateText.textContent !== w.date) this._dateText.textContent = w.date;
    }

    // The windows roll down from above: the day first, the date 140ms after it.
    _roll(delay) {
      var groups = [this._dayG, this._dateG];
      for (var i = 0; i < groups.length; i++) {
        var el = groups[i];
        if (el && el.animate) {
          el.animate(
            [{ transform: ROLL_FROM }, { transform: 'translateY(0px)' }],
            { duration: ROLL_MS, delay: delay + i * ROLL_GAP_MS, easing: ROLL_EASE, fill: 'backwards' }
          );
        }
      }
    }
  }

  if (FEATURE_ENABLED && window.customElements && !window.customElements.get(TAG)) {
    window.customElements.define(TAG, BathClock);
  }
})();
